import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  PhoneNumber,
  AvailablePhoneNumber,
  PhoneNumberSearchOptions,
  PhoneNumberPurchaseRequest,
  PhoneNumberProvider,
  PhoneNumberCapability,
} from '../types/phoneNumber';

export function usePhoneNumbers() {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [availableNumbers, setAvailableNumbers] = useState<
    AvailablePhoneNumber[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchPhoneNumbers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Only fetch Twilio numbers from our database
      const { data, error } = await supabase
        .from('phone_numbers')
        .select('*')
        .eq('provider', 'twilio')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform database data to match PhoneNumber interface
      const transformedNumbers: PhoneNumber[] = (data || []).map((row) => ({
        id: row.id,
        phoneNumber: row.phone_number,
        vapiId: row.vapi_id,
        friendlyName: row.friendly_name,
        provider: 'twilio' as PhoneNumberProvider,
        capabilities: (row.capabilities || []) as PhoneNumberCapability[],
        status: (row.status || 'active') as 'active' | 'inactive' | 'pending',
        region: row.region,
        locality: row.locality,
        rateCenter: row.rate_center,
        latitude: row.latitude,
        longitude: row.longitude,
        beta: row.beta || false,
        smsUrl: row.sms_url,
        voiceUrl: row.voice_url,
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
      }));

      setPhoneNumbers(transformedNumbers);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch phone numbers';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const searchAvailableNumbers = async (
    searchOptions: PhoneNumberSearchOptions = {},
  ) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'get-phone-numbers',
        {
          body: {
            provider: 'twilio',
            action: 'search',
            searchOptions,
          },
        },
      );

      if (error) throw error;

      if (data.success) {
        setAvailableNumbers(data.availableNumbers || []);
      } else {
        throw new Error(data.error || 'Failed to search available numbers');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to search available numbers';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const purchasePhoneNumber = async (request: PhoneNumberPurchaseRequest) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'purchase-phone-number',
        {
          body: request,
        },
      );

      if (error) throw error;

      if (data.success) {
        toast({
          title: 'Success',
          description: `Phone number ${request.phoneNumber} purchased successfully!`,
        });

        // Refresh the phone numbers list
        await fetchPhoneNumbers();

        return data.phoneNumber;
      } else {
        throw new Error(data.error || 'Failed to purchase phone number');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to purchase phone number';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    // Remove any non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Format US phone numbers
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1);
      return `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    } else if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    // For international numbers, just add the + if missing
    return cleaned.startsWith('+') ? phoneNumber : `+${cleaned}`;
  };

  const validatePhoneNumber = (phoneNumber: string) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  const fetchTwilioNumbers = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'fetch-twilio-numbers',
      );

      if (error) throw error;

      if (data.success) {
        return data.numbers || [];
      } else {
        throw new Error(data.error || 'Failed to fetch Twilio numbers');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch Twilio numbers';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const importSelectedNumbers = async (
    selectedNumbers: any[],
    vapiKeyName: string,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'import-twilio-numbers',
        {
          body: { selectedNumbers, vapiKeyName },
        },
      );

      if (error) throw error;

      if (data.success) {
        // Refresh the phone numbers list to show newly imported numbers
        await fetchPhoneNumbers();
        return data;
      } else {
        throw new Error(data.error || 'Failed to import numbers');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to import numbers';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    phoneNumbers,
    availableNumbers,
    loading,
    error,
    fetchPhoneNumbers,
    searchAvailableNumbers,
    purchasePhoneNumber,
    formatPhoneNumber,
    validatePhoneNumber,
    fetchTwilioNumbers,
    importSelectedNumbers,
  };
}
